import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Typography,
  Box,
  alpha,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import NavigatorLink from 'views/common/components/Logical/NavigatorLink';

const PREFIX = 'ERPagination';

const classes = {
  paginationUlOverride: `${PREFIX}-paginationUlOverride`,
  numberButtonOverride: `${PREFIX}-numberButtonOverride`,
  selectedButton: `${PREFIX}-selectedButton`,
  arrowBox: `${PREFIX}-arrowBox`,
  prevBox: `${PREFIX}-prevBox`,
  nextBox: `${PREFIX}-nextBox`,
  arrowIcon: `${PREFIX}-arrowIcon`,
  prevIcon: `${PREFIX}-prevIcon`,
  nextIcon: `${PREFIX}-nextIcon`,
  arrowText: `${PREFIX}-arrowText`,
  arrowLink: `${PREFIX}-arrowLink`,
  disabledArrowText: `${PREFIX}-disabledArrowText`,
  disabledLink: `${PREFIX}-disabledLink`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.paginationUlOverride}`]: {
    justifyContent: 'center',
  },

  [`& .${classes.numberButtonOverride}`]: {
    color: theme.palette.secondary.dark,
    fontSize: theme.typography.pxToRem(16),
    borderRadius: '0',
    padding: `${theme.typography.pxToRem(6)} ${theme.typography.pxToRem(8)}`,
    margin: `0 ${theme.typography.pxToRem(8)}`,
    minWidth: 20,
    '&:hover': {
      background: 'transparent',
    },
  },

  [`& .${classes.selectedButton}`]: {
    color: theme.palette.common.black,
    fontWeight: 700,
    background: 'transparent !important',
    '&:hover': {
      background: 'transparent !important',
    },
    [theme.breakpoints.up('md')]: {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
  },

  [`& .${classes.arrowBox}`]: {},
  [`& .${classes.prevBox}`]: {},
  [`& .${classes.nextBox}`]: {},

  [`& .${classes.arrowIcon}`]: {
    color: theme.palette.grey[700],
    width: theme.typography.pxToRem(26),
    height: theme.typography.pxToRem(26),
  },

  [`& .${classes.prevIcon}`]: {
    marginRight: theme.typography.pxToRem(6),
    transform: 'rotate(180deg)',
  },

  [`& .${classes.nextIcon}`]: {
    marginLeft: theme.typography.pxToRem(6),
  },

  [`& .${classes.arrowText}`]: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: '500',
    textTransform: 'uppercase',
  },

  [`& .${classes.arrowLink}`]: {
    color: 'inherit',
  },

  [`& .${classes.disabledArrowText}`]: {
    color: alpha(theme.palette.common.black, 0.26),
  },

  [`& .${classes.disabledLink}`]: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
}));

const ERPagination = ({
  data, url, link, handleChange,
}) => {
  const { t } = useTranslation('fe_er_pagination');
  const theme = useTheme();
  const isSmallDown = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Root>
      <Pagination
        classes={{
          ul: classes.paginationUlOverride,
        }}
        page={data.active_page}
        count={data.pages_count}
        boundaryCount={1}
        renderItem={(item) => (
          <>
            {item.type == 'page' && (
              <>
                {isSmallDown ? (
                  <>
                    {item.selected ? (
                      <PaginationItem
                        {...item}
                        className={`${
                          item.selected ? classes.selectedButton : ''
                        } ${classes.numberButtonOverride}`}>
                        {item.page}
                      </PaginationItem>
                    ) : (
                      ''
                    )}
                    {item.page == data.pages_count && (
                      <>
                        /
                        <PaginationItem
                          {...item}
                          classes={{ root: classes.numberButtonOverride }}>
                          {data.pages_count}
                        </PaginationItem>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <PaginationItem
                      type="page"
                      className={`${classes.numberButtonOverride}`}
                      classes={{
                        selected: classes.selectedButton,
                      }}
                      component={link ? NavigatorLink : Button}
                      to={link && `${url}?page=${item.page}`}
                      href={link && `${url}?page=${item.page}`}
                      {...item}
                      onClick={() => handleChange(item.page)}
                    />
                  </>
                )}
              </>
            )}
            {item.type == 'previous' && !item.disabled && (
              <>
                {link ? (
                  <NavigatorLink
                    href={{ url, query: { page: item.page } }}
                    className={`${classes.arrowLink} ${
                      item.disabled ? classes.disabledLink : ''
                    }`}
                    onClick={() => handleChange(item.page)}>
                    <Box
                      display="flex"
                      className={clsx(classes.arrowBox, classes.prevBox)}>
                      <ArrowRightAltIcon
                        className={`${classes.arrowIcon} ${classes.prevIcon}
                          ${item.disabled ? classes.disabledArrowText : ''}`}
                      />
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}>
                        {t('fe_er_pagination:prev')}
                      </Typography>
                    </Box>
                  </NavigatorLink>
                ) : (
                  <Button
                    className={classes.arrowLink}
                    onClick={() => {
                      handleChange(item.page);
                    }}>
                    <Box
                      display="flex"
                      className={clsx(classes.arrowBox, classes.prevBox)}>
                      <ArrowRightAltIcon
                        className={`${clsx(
                          classes.arrowIcon,
                          classes.prevIcon,
                        )}`}
                      />
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}>
                        {t('fe_er_pagination:prev')}
                      </Typography>
                    </Box>
                  </Button>
                )}
              </>
            )}
            {item.type == 'next' && !item.disabled && (
              <>
                {link ? (
                  <NavigatorLink
                    href={{ url, query: { page: item.page } }}
                    className={`${classes.arrowLink} ${
                      item.disabled ? classes.disabledLink : ''
                    }`}
                    onClick={() => handleChange(item.page)}>
                    <Box
                      display="flex"
                      className={`${clsx(classes.arrowBox, classes.nextBox)} ${
                        item.disabled ? classes.disabledArrowText : ''
                      }`}>
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}>
                        {t('fe_er_pagination:next')}
                      </Typography>
                      <ArrowRightAltIcon
                        className={`${classes.arrowIcon} ${classes.nextIcon}
                        ${item.disabled ? classes.disabledArrowText : ''}`}
                      />
                    </Box>
                  </NavigatorLink>
                ) : (
                  <Button
                    className={classes.arrowLink}
                    onClick={() => {
                      handleChange(item.page);
                    }}>
                    <Box
                      display="flex"
                      className={clsx(classes.arrowBox, classes.nextBox)}>
                      <Typography
                        className={`${classes.arrowText} ${
                          item.disabled ? classes.disabledArrowText : ''
                        }`}>
                        {t('fe_er_pagination:next')}
                      </Typography>
                      <ArrowRightAltIcon
                        className={clsx(classes.arrowIcon, classes.nextIcon)}
                      />
                    </Box>
                  </Button>
                )}
              </>
            )}
            {(item.type == 'start-ellipsis'
              || item.type === 'end-ellipsis') && (
              <Typography
                variant="span"
                sx={{ display: { xs: 'none', md: 'block' } }}>
                …
              </Typography>
            )}
          </>
        )}
      />
    </Root>
  );
};
export default ERPagination;
